// EMPTY WEDGE STYLE — the one spec for "this segment has no color yet".
// ==========================================================================
// PROBLEM (Gordon 2026-07-24, verbatim): "Maybe the empty state for a Wheel
// shouldn't be 'light gray' because a segment CAN be light gray - maybe it
// should be transparent with an outline. And it would need to match the same on
// View Wheel and on Edit Wheel."
//
// He is right, and the source proves it. The empty/colorless fallback is
// `#e1e1e1` (createPie.js DEFAULT_WEDGE_COLOR, renderer-v2 DEFAULTS.wedgeColor,
// the v1 `emptyChartData` blank ring). Meanwhile the segment palette hands out
// `#FFFFFF` and a whole family of near-whites and pale grays as PERFECTLY VALID
// segment colors. So the "empty" state is drawn from the SAME value space it
// has to be distinguishable from: a wedge the user deliberately painted pale
// gray and a wedge that has no color at all are visually the same wedge. No
// amount of re-picking a gray fixes that — any gray we choose is a gray a user
// can also choose.
//
// FIX — leave the color space entirely. An empty wedge renders with a
// TRANSPARENT fill and a DASHED outline. Nothing in the palette can produce a
// dashed edge, so "empty" stops being a color and becomes a TREATMENT: it is
// unambiguous at a glance even next to a legitimately pale wedge.
//
// This module owns the spec + the flag ONLY (pure, no DOM). Every renderer
// imports it — never re-derives it — so the v1 D3 engine (createPie: gallery /
// Explore card thumbnails, the Edit-Wheel builder canvas, Segment View), the
// canonical renderer-v2 View-Wheel engine, and the embed widget cannot silently
// diverge. That parity is the whole point: the standing law is that a Wheel
// looks identical on every renderer, and the previous #e1e1e1 value was itself
// chosen (see createPie's DEFAULT_WEDGE_COLOR comment) purely to keep thumbnails
// matching the full wheel. Replacing it in one engine only would re-open exactly
// the drift that comment was written to close.
//
// Flag convention mirrors segmentContrastGuard.js / constants/featureFlags.js: a
// build-time REACT_APP_* var, default OFF, so OFF == today, byte-identical.

const truthy = (v) => v === "true" || v === "1" || v === true;

// EMPTY_WEDGE_OUTLINE_ENABLED — when ON, a colorless/blank wedge renders
// transparent-with-a-dashed-outline instead of the ambiguous #e1e1e1 gray. OFF
// (default) leaves every renderer byte-identical to today.
//
// TWO build vars, one decision. The app bundle (CRA) reads REACT_APP_*; the
// embed widget is a SEPARATE standalone esbuild bundle with its own define
// namespace (src/embed/build.mjs) and reads EMBED_*. This module is imported by
// BOTH — renderer-v2/engine.js is bundled into the embed — so each reference is
// wrapped in a `typeof process` guard: in the embed bundle esbuild substitutes
// the EMBED_* literal and the un-defined REACT_APP_* reference is never
// evaluated (the guard short-circuits), so there is no bare-`process`
// ReferenceError in a browser bundle.
const envFlag = (read) => {
  try {
    return typeof process !== "undefined" && process.env ? read() : undefined;
  } catch (e) {
    return undefined;
  }
};

export const EMPTY_WEDGE_OUTLINE_ENABLED =
  truthy(envFlag(() => process.env.REACT_APP_EMPTY_WEDGE_OUTLINE)) ||
  truthy(envFlag(() => process.env.EMBED_EMPTY_WEDGE_OUTLINE));

// --------------------------------------------------------------------------
// The spec (not magic numbers).

// EMPTY_WEDGE_FILL — the literal paint value for an empty wedge.
//
// CRITICAL, do not "simplify" this to `none`: SVG hit-testing under the initial
// `pointer-events: visiblePainted` treats an element as a pointer target over
// its fill area only when `fill` is a value OTHER than `none`. `transparent`
// (= rgba(0,0,0,0)) IS such a value, so the wedge stays fully clickable,
// hoverable and DRAGGABLE — which the Edit-Wheel builder depends on (select a
// segment, drag to reorder, hover tooltip). `fill="none"` would render the same
// and silently make every empty segment in the builder dead to the mouse.
export const EMPTY_WEDGE_FILL = "transparent";

// EMPTY_WEDGE_OUTLINE_COLOR — a single mid-gray, deliberately NOT theme-split
// (this product has no light/dark mode — standing law). Relative luminance
// ~0.25 puts it near the midpoint of the range, so it reads against BOTH ends:
// ~3.9:1 against a white page/card and ~6.1:1 against black. One value, legible
// on the white gallery card, on the builder canvas, and on a dark wallpaper.
export const EMPTY_WEDGE_OUTLINE_COLOR = "#8a8a8a";

// EMPTY_WEDGE_OUTLINE_WIDTH — px. Matches the wedge divider width so the empty
// outline reads as the same weight of line as the rest of the wheel's geometry.
export const EMPTY_WEDGE_OUTLINE_WIDTH = 2;

// EMPTY_WEDGE_OUTLINE_DASH — the semantic carrier. A dashed edge is the
// universal "placeholder / nothing here yet" convention (drop zones, empty
// slots) and, more importantly here, is a signal the color palette CANNOT
// counterfeit: a pale-but-valid segment renders as a SOLID pale wedge with the
// normal solid divider, an empty one as a see-through wedge with a dashed edge.
// 6-on / 5-off stays readable at gallery-thumbnail scale without turning into a
// solid line at full View-Wheel scale.
export const EMPTY_WEDGE_OUTLINE_DASH = "6 5";

// --------------------------------------------------------------------------
// BUSY / IMAGE WALLPAPERS — why one flat gray is enough.
//
// The outline is drawn as a SEPARATE path INSET by half the wedge divider width
// (the same insetWedgeForHairline geometry the contrast guard uses), and the
// wedge path KEEPS its existing white divider stroke (v1 `.arc { stroke: white }`
// / v2 `theme.wedgeStroke`). So on a photo or pattern wallpaper the white
// divider sits immediately outside the dashed gray line and acts as a halo —
// the gray never has to survive directly against arbitrary photo pixels. That
// also means we never touch the divider, so an empty wedge keeps the exact same
// visible size as a filled one (the geometry rule Gordon set 2026-07-21 when the
// hairline work briefly changed wedge sizes).

/**
 * Is this v1 chart datum an EMPTY wedge?
 *
 * Two distinct things both count as "empty", and Gordon's complaint covers both:
 *  1. `isBlank` — the whole-wheel placeholder ring a wheel with zero segments
 *     draws (v1 `emptyChartData`, hardcoded #e1e1e1).
 *  2. no `color` — a real segment whose color is null/undefined/"" (the case
 *     DEFAULT_WEDGE_COLOR exists for; card-grid / gallery feeds carry these).
 *
 * A gradient / pattern / image / gif fill is NOT empty and is resolved BEFORE
 * this predicate is consulted in every caller.
 *
 * @param {{isBlank?: boolean, color?: string}} datum
 */
export const isEmptyWedgeDatum = (datum) => {
  if (!datum || typeof datum !== "object") return false;
  if (datum.isBlank === true) return true;
  return !datum.color;
};

/**
 * Resolve the enable decision. Production callers never pass an override, so
 * this collapses to the build-time flag (OFF ⇒ byte-identical). Fixtures/tests
 * pass one to toggle without a rebuild — same shape as
 * isSegmentHairlineEnabled(config.segmentHairline).
 */
export const isEmptyWedgeOutlineEnabled = (override) =>
  override === undefined || override === null
    ? EMPTY_WEDGE_OUTLINE_ENABLED
    : truthy(override);
